import { getLogger } from '@logtape/logtape';
import { createElement } from 'react';

import { CollectionSuccessNotification } from '@/components/collection-success-notification.tsx';

import { revalidateAppointmentsRangeCache } from '@/hooks/use-appointments-for-date-range.ts';
import useConfig from '@/hooks/useConfig.ts';
import { useEnrichedPractitionerDataForSpecificInstance } from '@/hooks/useEnrichedPractitionerData.ts';

import { NotificationType } from '@/models/Notification.ts';
import { BuildCollectionOptions, CollectionStatus } from '@/models/appointment/Appointment.ts';
import { PVSProvider } from '@/models/customers/PVSProvider.ts';

import { book_appointment_dto_schema } from '@/schemas/BookAppointmentDto.schema.ts';

import { createAppointmentSeries, getCorrectInsuranceForForms } from '@/services/appointmentService.ts';
import {
    hideNotification,
    showErrorNotification,
    showLoadingNotification,
    updateNotification,
} from '@/services/notificationService.ts';

import { selectCurrentForm, useBookingStore } from '@/stores/bookingStore.ts';

import {
    buildCollectionDto,
    createAppointmentDtoFromForm,
    resolveDocCirrusPvsUserId,
} from '@/utils/appointmentUtils.ts';
import { resolveBookingErrorMessage } from '@/utils/booking-error-utils.ts';
import { buildSeriesSummaryEntries, pluralizeTermine } from '@/utils/collection-utils.ts';
import { resolvePatientUserId } from '@/utils/patientUtils.ts';

const logger = getLogger(['hook', 'book-appointment-collection']);

interface BookCollectionResult {
    needsDuplication: boolean;
}

interface BookAppointmentCollectionHook {
    bookCollection: (options?: { onClose?: () => void; isOverlap?: boolean }) => Promise<BookCollectionResult | void>;
}

export const useBookAppointmentCollection = (): BookAppointmentCollectionHook => {
    const { selected_customer, selected_location } = useConfig();
    const config = useBookingStore((s) => s.collection_configuration);
    const currentForm = useBookingStore(selectCurrentForm);
    const currentPatient = useBookingStore((s) => s.current_patient);
    const { data: enrichedPractitionerData } = useEnrichedPractitionerDataForSpecificInstance(
        currentPatient?.instance_id ?? ''
    );

    const bookCollection: BookAppointmentCollectionHook['bookCollection'] = async (options) => {
        const { collection_occurrences: freshOccurrences } = useBookingStore.getState();

        if (!config || !currentForm || !currentPatient || !enrichedPractitionerData) {
            showErrorNotification('Fehlende Daten für die Terminserie');
            return undefined;
        }

        if (freshOccurrences.length === 0) {
            showErrorNotification('Keine Termine für die Serie vorhanden');
            return undefined;
        }

        const userId = resolvePatientUserId(currentPatient);
        if (!userId) {
            showErrorNotification('Benutzer-ID ist ungültig');
            return undefined;
        }

        useBookingStore.setState({ is_booking_loading: true, isCollectionSubmitting: true });

        const instanceId = currentPatient.instance_id;
        const buildCollectionOptions: BuildCollectionOptions = {
            ...(options?.isOverlap !== undefined && { isOverlap: options.isOverlap }),
        };

        let pvsUserId: string;
        let eternoUserId: string | undefined;
        if (enrichedPractitionerData.pvs_provider === PVSProvider.DOC_CIRRUS) {
            const { patient_duplication_allowed } = useBookingStore.getState();
            let result: Awaited<ReturnType<typeof getCorrectInsuranceForForms>> | undefined;
            let insuranceError = false;
            try {
                result = await getCorrectInsuranceForForms(
                    [{ ...currentForm, form_id: currentForm.id }],
                    selected_customer.customer_id,
                    instanceId,
                    userId,
                    patient_duplication_allowed
                );
            } catch (error) {
                insuranceError = true;
                logger.error('Insurance validation failed', { error: error });
            }

            if (insuranceError) {
                showErrorNotification('Fehler bei der Versicherungsprüfung. Bitte versuchen Sie es erneut.');
                useBookingStore.setState({
                    is_booking_loading: false,
                    isCollectionSubmitting: false,
                    is_validating_insurance: false,
                    patient_duplication_allowed: false,
                });
                return undefined;
            }

            if (!result) {
                showErrorNotification('Fehler bei der Versicherungsprüfung. Bitte versuchen Sie es erneut.');
                useBookingStore.setState({
                    is_booking_loading: false,
                    isCollectionSubmitting: false,
                    is_validating_insurance: false,
                    patient_duplication_allowed: false,
                });
                return undefined;
            }

            if (result.duplication_required && !result.duplication_done) {
                useBookingStore.setState({
                    is_booking_loading: false,
                    isCollectionSubmitting: false,
                    is_validating_insurance: false,
                    patient_duplication_allowed: false,
                });
                return { needsDuplication: true };
            }

            const resolved = resolveDocCirrusPvsUserId(result);
            if (!resolved.success) {
                showErrorNotification(resolved.errorMessage);
                useBookingStore.setState({
                    is_booking_loading: false,
                    isCollectionSubmitting: false,
                    is_validating_insurance: false,
                    patient_duplication_allowed: false,
                });
                return undefined;
            }
            pvsUserId = resolved.userId;
            eternoUserId = resolved.eternoUserId;
        } else {
            pvsUserId = currentPatient.pvs_user_id;
        }

        const baseDto = createAppointmentDtoFromForm(
            { ...currentForm, form_id: currentForm.id, pvs_user_id: pvsUserId, eterno_user_id: eternoUserId },
            enrichedPractitionerData,
            selected_location,
            currentPatient
        );
        if (!baseDto) {
            showErrorNotification('Fehler beim Erstellen der Termindaten');
            useBookingStore.setState({
                is_booking_loading: false,
                isCollectionSubmitting: false,
                is_validating_insurance: false,
                patient_duplication_allowed: false,
            });
            return undefined;
        }

        const dtoValidation = book_appointment_dto_schema.safeParse(baseDto);
        if (!dtoValidation.success) {
            logger.error('Invalid booking DTO', { error: dtoValidation.error });
            showErrorNotification('Fehler beim Erstellen der Termindaten');
            useBookingStore.setState({
                is_booking_loading: false,
                isCollectionSubmitting: false,
                is_validating_insurance: false,
                patient_duplication_allowed: false,
            });
            return undefined;
        }

        // Hoisted out of the try so the optional chaining doesn't bail out the React Compiler.
        const exitPlacementMode = options?.isOverlap ? options.onClose : undefined;

        let notificationId = '';
        try {
            notificationId = showLoadingNotification(`Buche ${freshOccurrences.length} Termine...`, 'Terminserie');

            const dto = buildCollectionDto(baseDto, config, freshOccurrences, instanceId, buildCollectionOptions);
            const response = await createAppointmentSeries(selected_customer.customer_id, baseDto.patient.id, dto);

            useBookingStore.setState({ is_validating_insurance: false, patient_duplication_allowed: false });

            if (response.collection.booking_status === CollectionStatus.FAILED) {
                hideNotification(notificationId);
                showErrorNotification('Fehler beim Buchen der Terminserie.');
                useBookingStore.setState({ is_booking_loading: false, isCollectionSubmitting: false });
                return undefined;
            }

            const bookedAppointments = [...response.pre_booked_appointments].sort((a, b) =>
                a.start.localeCompare(b.start)
            );

            updateNotification(NotificationType.SUCCESS, {
                id: notificationId,
                message: createElement(CollectionSuccessNotification, { appointments: bookedAppointments }),
                title: `Terminserie erfolgreich gebucht (${pluralizeTermine(bookedAppointments.length)})`,
            });
            revalidateAppointmentsRangeCache();

            useBookingStore
                .getState()
                .completeCollectionBooking('series', buildSeriesSummaryEntries(currentForm, bookedAppointments));

            if (exitPlacementMode) exitPlacementMode();
        } catch (err) {
            logger.error('Collection booking failed', { error: err });

            if (notificationId) hideNotification(notificationId);

            const errorMsg = resolveBookingErrorMessage({
                err: err,
                defaultMessage: 'Fehler beim Buchen der Terminserie. Bitte versuchen Sie es erneut.',
            });

            showErrorNotification(errorMsg);
            useBookingStore.setState({
                is_booking_loading: false,
                isCollectionSubmitting: false,
                is_validating_insurance: false,
                patient_duplication_allowed: false,
            });
        }

        return undefined;
    };

    return { bookCollection };
};
